import { useCallback, useEffect, useRef, useState } from "react";

import { isElectron } from "../../env";
import { transcribeComposerDictation } from "../../morrowDictation";
import {
  collectFinalSpeechTranscript,
  collectInterimSpeechTranscript,
  DICTATION_INTERIM_MS,
  DICTATION_MIN_CHUNK_BYTES,
  DICTATION_SEGMENT_MS,
  ensureDictationMicrophoneAccess,
  getSpeechRecognitionConstructor,
  microphoneAccessErrorKey,
  pickDictationRecorderMimeType,
  shouldPreferServerDictation,
  speechRecognitionErrorKey,
  type DictationErrorKey,
} from "./composerDictation";

type MicrophoneTrack = { stop: () => void };
type MicrophoneStream = {
  getTracks: () => MicrophoneTrack[];
};

type DictationRecorder = {
  state: string;
  // Passing a timeslice makes `ondataavailable` fire periodically WITHOUT
  // stopping the recorder, which is what lets us preview interim transcripts.
  start: (timeslice?: number) => void;
  stop: () => void;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
};

export function useComposerDictation(options: {
  disabled: boolean;
  lang?: string;
  onAppendText: (text: string) => void;
  onError: (title: DictationErrorKey) => void;
}) {
  const [isListening, setIsListening] = useState(false);
  // Live, not-yet-final transcript shown while the user speaks ("keep writing").
  const [interimText, setInterimText] = useState("");
  const recognitionRef = useRef<{
    start: () => void;
    stop: () => void;
    abort: () => void;
  } | null>(null);
  const recorderRef = useRef<DictationRecorder | null>(null);
  const streamRef = useRef<MicrophoneStream | null>(null);
  const chunkTimerRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Server (Cloudflare Whisper) dictation keeps the mic open and rotates short
  // segments. These refs drive the live interim preview inside a segment.
  const segmentChunksRef = useRef<Blob[]>([]);
  const segmentSeqRef = useRef(0);
  const interimAbortRef = useRef<AbortController | null>(null);
  const interimInFlightRef = useRef(false);
  const wantListeningRef = useRef(false);
  const startGenerationRef = useRef(0);
  const startingRef = useRef(false);
  const receivedBrowserSpeechRef = useRef(false);
  const onAppendTextRef = useRef(options.onAppendText);
  const onErrorRef = useRef(options.onError);
  const langRef = useRef(options.lang);

  onAppendTextRef.current = options.onAppendText;
  onErrorRef.current = options.onError;
  langRef.current = options.lang;

  const clearChunkTimer = () => {
    if (chunkTimerRef.current !== null) {
      window.clearTimeout(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
  };

  const abortInterim = () => {
    interimAbortRef.current?.abort();
    interimAbortRef.current = null;
    interimInFlightRef.current = false;
  };

  const releaseMicrophone = () => {
    clearChunkTimer();
    abortInterim();
    segmentChunksRef.current = [];
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
    }
  };

  const stopMicrophone = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    abortInterim();
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state === "recording") {
      try {
        recorder.stop();
      } catch {
        // Already stopped.
      }
    }
    releaseMicrophone();
  };

  const stop = useCallback(() => {
    wantListeningRef.current = false;
    startingRef.current = false;
    receivedBrowserSpeechRef.current = false;
    setIsListening(false);
    setInterimText("");
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    recognition?.stop();
    clearChunkTimer();
    const recorder = recorderRef.current;
    if (recorder && recorder.state === "recording") {
      try {
        recorder.stop();
      } catch {
        stopMicrophone();
      }
      return;
    }
    stopMicrophone();
  }, []);

  // Live preview: transcribe the cumulative audio of the CURRENT segment and
  // show it in the "Listening…" banner (never committed to the composer). This
  // is best-effort — a failed interim never surfaces an error; the segment's
  // final commit is what actually inserts text.
  const runInterimTranscription = (
    generation: number,
    seq: number,
    mimeType: string | undefined,
  ) => {
    if (generation !== startGenerationRef.current || !wantListeningRef.current) {
      return;
    }
    // One interim request at a time: cumulative audio only grows, so a stale
    // in-flight request is always superseded by the next slice anyway.
    if (interimInFlightRef.current) {
      return;
    }
    const blob = new Blob(segmentChunksRef.current, mimeType ? { type: mimeType } : undefined);
    if (blob.size < DICTATION_MIN_CHUNK_BYTES) {
      return;
    }
    interimInFlightRef.current = true;
    interimAbortRef.current?.abort();
    const abort = new AbortController();
    interimAbortRef.current = abort;
    void transcribeComposerDictation({ audio: blob, signal: abort.signal })
      .then((text) => {
        if (
          text.length > 0 &&
          seq === segmentSeqRef.current &&
          generation === startGenerationRef.current &&
          wantListeningRef.current
        ) {
          setInterimText(text);
        }
      })
      .catch(() => {
        // Interim is display-only; swallow network/aborts and let the final
        // segment commit report any real failure.
      })
      .finally(() => {
        if (interimAbortRef.current === abort) {
          interimInFlightRef.current = false;
        }
      });
  };

  // Commit a finished segment's transcript into the composer.
  const commitSegment = async (generation: number, seq: number, blob: Blob) => {
    if (blob.size < DICTATION_MIN_CHUNK_BYTES) {
      if (seq === segmentSeqRef.current && generation === startGenerationRef.current) {
        setInterimText("");
      }
      return;
    }
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      // Do NOT pass a language hint: let Whisper auto-detect the spoken language.
      // Forcing the browser locale here is exactly what mis-transcribed users who
      // speak a language different from their browser UI.
      const text = await transcribeComposerDictation({ audio: blob, signal: abort.signal });
      if (generation !== startGenerationRef.current) {
        return;
      }
      if (text.length > 0) {
        onAppendTextRef.current(text);
      }
      // Only clear the preview if a newer segment hasn't already taken over the
      // banner — otherwise this stale commit would wipe the live preview.
      if (seq === segmentSeqRef.current) {
        setInterimText("");
      }
    } catch (cause) {
      if (abort.signal.aborted || generation !== startGenerationRef.current) {
        return;
      }
      wantListeningRef.current = false;
      startingRef.current = false;
      setIsListening(false);
      setInterimText("");
      stopMicrophone();
      onErrorRef.current(
        cause instanceof TypeError ? "composer.dictationNetwork" : "composer.dictationFailed",
      );
    }
  };

  const startServerSegment = (
    generation: number,
    stream: MicrophoneStream,
    mimeType: string | undefined,
  ) => {
    if (generation !== startGenerationRef.current || !wantListeningRef.current) {
      return;
    }
    const seq = (segmentSeqRef.current += 1);
    const Recorder = MediaRecorder as unknown as new (
      mediaStream: MicrophoneStream,
      options?: { mimeType?: string },
    ) => DictationRecorder;
    const recorder = mimeType ? new Recorder(stream, { mimeType }) : new Recorder(stream);
    segmentChunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        segmentChunksRef.current.push(event.data);
        // Preview the cumulative segment audio so far (webm/opus keeps its header
        // in the first slice, so [slice0..sliceN] is always decodable).
        runInterimTranscription(generation, seq, mimeType);
      }
    };
    recorder.onstop = () => {
      if (recorderRef.current === recorder) {
        recorderRef.current = null;
      }
      clearChunkTimer();
      // The finished segment's own interim requests are moot now.
      interimAbortRef.current?.abort();
      interimInFlightRef.current = false;
      const blob = new Blob(segmentChunksRef.current, mimeType ? { type: mimeType } : undefined);
      segmentChunksRef.current = [];
      const shouldContinue = generation === startGenerationRef.current && wantListeningRef.current;
      // Open the next segment immediately so we drop as little speech as possible
      // at the rotation boundary; the finished segment transcribes in parallel.
      if (shouldContinue) {
        startServerSegment(generation, stream, mimeType);
      } else if (!wantListeningRef.current) {
        releaseMicrophone();
      }
      void commitSegment(generation, seq, blob);
    };
    recorderRef.current = recorder;
    try {
      // Timeslice → periodic `ondataavailable` without stopping the recorder.
      recorder.start(DICTATION_INTERIM_MS);
    } catch {
      wantListeningRef.current = false;
      startingRef.current = false;
      setIsListening(false);
      stopMicrophone();
      onErrorRef.current("composer.dictationFailed");
      return;
    }
    clearChunkTimer();
    chunkTimerRef.current = window.setTimeout(() => {
      if (recorderRef.current === recorder && recorder.state === "recording") {
        recorder.stop();
      }
    }, DICTATION_SEGMENT_MS);
  };

  const startServerDictation = async (generation: number) => {
    if (generation !== startGenerationRef.current || !wantListeningRef.current) {
      return;
    }
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      wantListeningRef.current = false;
      startingRef.current = false;
      onErrorRef.current("composer.dictationUnsupported");
      return;
    }

    const previousRecognition = recognitionRef.current;
    recognitionRef.current = null;
    previousRecognition?.abort();

    let stream: MicrophoneStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (cause) {
      if (generation !== startGenerationRef.current) {
        return;
      }
      wantListeningRef.current = false;
      startingRef.current = false;
      onErrorRef.current(microphoneAccessErrorKey(cause));
      return;
    }
    if (generation !== startGenerationRef.current || !wantListeningRef.current) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
      return;
    }

    streamRef.current = stream;
    startingRef.current = false;
    setIsListening(true);
    startServerSegment(generation, stream, pickDictationRecorderMimeType());
  };

  const start = useCallback(() => {
    if (options.disabled || startingRef.current) {
      return;
    }
    const SpeechRecognition = getSpeechRecognitionConstructor();
    // Prefer the browser Speech API when it's available: it streams interim
    // results so the user sees their words transcribe live as they speak (the
    // server Whisper path is batched every few seconds with no live feedback).
    // We set `recognition.lang` from the composer locale below (not raw
    // navigator.language), so multi-language dictation still works. Electron /
    // browsers without SpeechRecognition fall through to the server Whisper path.
    const preferServer = shouldPreferServerDictation({
      isElectron,
      hasSpeechRecognition: SpeechRecognition !== null,
    });
    if (!preferServer && !SpeechRecognition) {
      onErrorRef.current("composer.dictationUnsupported");
      return;
    }

    const generation = startGenerationRef.current + 1;
    startGenerationRef.current = generation;
    startingRef.current = true;
    wantListeningRef.current = true;
    receivedBrowserSpeechRef.current = false;
    abortRef.current?.abort();
    abortRef.current = null;

    void (async () => {
      // Server dictation opens the mic itself. Probing first then re-acquiring
      // races the device on macOS/Electron and surfaces as a generic start failure.
      if (preferServer || !SpeechRecognition) {
        await startServerDictation(generation);
        return;
      }

      const microphoneError = await ensureDictationMicrophoneAccess();
      if (generation !== startGenerationRef.current || !wantListeningRef.current) {
        return;
      }
      if (microphoneError) {
        wantListeningRef.current = false;
        startingRef.current = false;
        onErrorRef.current(microphoneError);
        return;
      }

      recognitionRef.current?.abort();
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      // Interim results stream the words as they're spoken so the user sees live
      // "listening + writing" feedback; final results are what we commit.
      recognition.interimResults = true;
      recognition.lang =
        options.lang ?? (typeof navigator === "undefined" ? "en" : navigator.language || "en");
      recognition.onresult = (event) => {
        const finalText = collectFinalSpeechTranscript(event);
        if (finalText.trim().length > 0) {
          receivedBrowserSpeechRef.current = true;
          onAppendTextRef.current(finalText);
        }
        // Show whatever is still in-progress; once a segment finalizes it moves
        // into the prompt above and its interim preview clears.
        setInterimText(collectInterimSpeechTranscript(event));
      };
      recognition.onerror = (event) => {
        // Chromium reports `network` when Google's speech service is missing
        // (Electron, Brave) or briefly unreachable. Fall back before toasting
        // if we never received a result; if we did, let onend restart.
        if (event.error === "network" && !receivedBrowserSpeechRef.current) {
          void startServerDictation(generation);
          return;
        }
        if (event.error === "network" && receivedBrowserSpeechRef.current) {
          return;
        }
        const key = speechRecognitionErrorKey(event.error);
        if (key) {
          wantListeningRef.current = false;
          startingRef.current = false;
          setIsListening(false);
          setInterimText("");
          onErrorRef.current(key);
        }
      };
      recognition.onend = () => {
        if (recognitionRef.current !== recognition || !wantListeningRef.current) {
          startingRef.current = false;
          if (recognitionRef.current !== recognition) {
            return;
          }
          setIsListening(false);
          setInterimText("");
          return;
        }
        try {
          recognition.start();
        } catch {
          wantListeningRef.current = false;
          startingRef.current = false;
          setIsListening(false);
        }
      };

      recognitionRef.current = recognition;
      setIsListening(true);
      startingRef.current = false;
      try {
        recognition.start();
      } catch {
        wantListeningRef.current = false;
        setIsListening(false);
        onErrorRef.current("composer.dictationFailed");
      }
    })();
  }, [options.disabled, options.lang]);

  const toggle = useCallback(() => {
    if (isListening || startingRef.current) {
      stop();
      return;
    }
    start();
  }, [isListening, start, stop]);

  useEffect(() => {
    if (options.disabled && wantListeningRef.current) {
      stop();
    }
  }, [options.disabled, stop]);

  useEffect(() => {
    return () => {
      wantListeningRef.current = false;
      startGenerationRef.current += 1;
      recognitionRef.current?.abort();
      stopMicrophone();
    };
  }, []);

  return {
    isListening,
    interimText,
    isSupported:
      getSpeechRecognitionConstructor() !== null ||
      (typeof MediaRecorder !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia)),
    toggle,
    stop,
  };
}
